import { useOnValueChange } from '@calendly/hooks';
import { I18n } from '@calendly/i18n';
import { Avatar } from '@calendly/ui/components/avatar';
import { BareButton } from '@calendly/ui/components/bare-button';
import { BodyText } from '@calendly/ui/components/typography';
import { colorTextSecondary } from '@calendly/ui/theme';
import classNames from 'classnames';
import { createRef, useState, forwardRef } from 'react';
import { useGetUserQuery } from 'common';
import { extractHosts } from 'components/meetings/hooks/helpers';
import { useRightRail } from 'features/right_rail';
import { WidgetContainer } from './widget_container';
const styledHostsContainerCss = "soai5fo";
const StyledHostsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(styledHostsContainerCss, props.className)} />;
});
const hostAvatarWrapperCss = "h1o3k1nu";
const HostAvatarWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(hostAvatarWrapperCss, props.className)} />;
});
const hostListItemCss = "h1a8td0n";
const HostListItem = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(hostListItemCss, props.className)} />;
});
const nameWrapperCss = "nk336m9";
const NameWrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(nameWrapperCss, props.className)} />;
});
const HOSTS_TO_SHOW_INCREMENT = 5;
const minimalButtonCss = "mnjfdbu";
const MinimalButton = forwardRef((props, ref) => <BareButton ref={ref} {...props} className={classNames(minimalButtonCss, props.className)} />);
const styledDivCss = "s1el167f";
const StyledDiv = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(styledDivCss, props.className)} />;
});

/**
 * Normalizes host data from either the old schema or new GraphQL schema
 * @param {Object} host - Host object from either schema
 * @returns {Object} Normalized host object with consistent structure
 */
const normalizeHost = host => {
  if (!host) return null;

  // New schema: hosts from eventMemberships/attendees have kind, uuid, name
  // Old schema: hosts from pageMembers/memberships have userUuid, uuid, name, profile
  return {
    uuid: host.uuid || host.userUuid,
    name: host.name || '',
    avatarLetter: host.avatarLetter || host.profile?.avatarLetter,
    avatarUrl: host.avatarUrl || host.profile?.avatarUrl,
    kind: host.kind // HOST, COHOST, etc. (new schema)
  };
};
const HostAvatars = ({
  hosts
}) => {
  const {
    data: currentUser
  } = useGetUserQuery();
  const {
    current,
    openOrReplace
  } = useRightRail();

  // Normalize all hosts to consistent structure
  const normalizedHosts = hosts && Array.isArray(hosts) ? hosts.map(normalizeHost).filter(Boolean) : [];
  const [isExpanded, setExpanded] = useState(normalizedHosts.length <= HOSTS_TO_SHOW_INCREMENT);
  const [numHostsToShow, setNumHostsToShow] = useState(HOSTS_TO_SHOW_INCREMENT);
  const buttonRefs = normalizedHosts.map(() => createRef());
  const [openedHostIndex, setOpenedHostIndex] = useState(-1);
  useOnValueChange(current, () => {
    if (!current) {
      buttonRefs[openedHostIndex]?.current?.focus();
      setOpenedHostIndex(-1);
    }
  });
  if (normalizedHosts.length === 0) {
    return null;
  }
  const hostsToShow = isExpanded ? normalizedHosts : normalizedHosts.slice(0, numHostsToShow);
  const moreHostsCount = normalizedHosts.length - hostsToShow.length >= HOSTS_TO_SHOW_INCREMENT ? HOSTS_TO_SHOW_INCREMENT : normalizedHosts.length - hostsToShow.length;
  const showMoreButtonText = String(I18n.t('publisher.meetings.hosts.view_more_hosts', {
    count: moreHostsCount
  }));
  const handleShowMore = () => {
    const newHostCount = numHostsToShow + HOSTS_TO_SHOW_INCREMENT;
    if (newHostCount >= normalizedHosts.length) {
      setExpanded(true);
      setNumHostsToShow(normalizedHosts.length);
    } else {
      setNumHostsToShow(newHostCount);
    }
  };
  const onOpen = (uuid, index) => {
    setOpenedHostIndex(index);
    openOrReplace('profile', {
      profile: uuid
    });
  };
  return <HostAvatarWrapper>
      {hostsToShow.map((host, index) => {
      // Support both old schema (avatarLetter, profile.avatarLetter) and new schema (derive from name)
      const avatarLetter = host.avatarLetter || host.profile?.avatarLetter || (host.name ? host.name.charAt(0).toUpperCase() : '');
      const avatarUrl = host.avatarUrl || host.profile?.avatarUrl;
      return <HostListItem key={host.uuid || host.userUuid || host.userId || `host-${index}`}>
            <Avatar size="small" alt={host.name} letter={avatarLetter} src={avatarUrl} />
            <NameWrapper>
              <BodyText weight="bold">
                <MinimalButton onClick={() => onOpen(host.uuid, index)} ref={buttonRefs[index]}>
                  {host.name}
                </MinimalButton>
              </BodyText>
              {currentUser?.uuid && host.uuid === currentUser.uuid && <StyledDiv>
                  <BodyText size="x-small" color={colorTextSecondary}>
                    {I18n.t('publisher.event_types.one_off.creation_modal.details_step.meeting_summary.member_owner')}
                  </BodyText>
                </StyledDiv>}
            </NameWrapper>
          </HostListItem>;
    })}
      {!isExpanded && <BareButton onClick={handleShowMore}>
          {showMoreButtonText}
        </BareButton>}
    </HostAvatarWrapper>;
};
const MeetingHosts = ({
  event
}) => {
  const hosts = extractHosts(event);
  if (!hosts || hosts.length === 0) {
    return null;
  }
  return <StyledHostsContainer>
      <HostAvatars hosts={hosts} />
    </StyledHostsContainer>;
};
export const HostsWidget = ({
  event
}) => {
  const hosts = extractHosts(event);
  if (!hosts || hosts.length === 0) {
    return null;
  }
  return <WidgetContainer title={I18n.t('publisher.event_types.hosts')} noPadding>
      <MeetingHosts event={event} />
    </WidgetContainer>;
};

require("./hosts_widget.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./hosts_widget.js");